/**
 * GlobalRunProgress
 *
 * Full progress bar + details panel shown on all pages EXCEPT AutomationPage
 * when a test run is active. Reuses the same RunProgressBar and RunDetailsPanel
 * components for visual consistency.
 *
 * Rendered inside main-content-card (via MainLayout) with sticky
 * positioning. Fires a toast notification when the run completes.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import {
  useIsRunActive,
  useIsRunning,
  useIsCompleted,
  useHasFailures,
  useRunProgress,
  useActiveRunMeta,
  useRunError,
  useRunState,
  useRunActions,
  useWorkerStatus,
} from '../../store/automation/runStore';
import RunProgressBar from '../../features/automationScripts/components/RunProgressBar';
import RunDetailsPanel from '../../features/automationScripts/components/RunDetailsPanel';
import { useToastNotificationContext } from '../notifications/ToastNotificationContext';
import { runStreamService } from '../workers/RunStreamService';

/**
 * Build the automation page URL from run metadata.
 */
const buildRunUrl = (meta) => {
  if (!meta?.runnableFilePath) return null;
  const { projectId, runnableFilePath } = meta;
  const parts = runnableFilePath.split('/');
  const projectNameIndex = 2;
  const folderParts = parts.slice(projectNameIndex + 1, -1);
  const folderPath = folderParts.join('/');
  return `/automation-script/${projectId}/${encodeURIComponent(folderPath)}`;
};

const GlobalRunProgress = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const hasNotifiedRef = useRef(false);
  const lastMetaRef = useRef(null);

  const isRunActive = useIsRunActive();
  const isRunning = useIsRunning();
  const isCompleted = useIsCompleted();
  const hasFailures = useHasFailures();
  const { passed, failed, total, skipped, disabled } = useRunProgress();
  const activeRunMeta = useActiveRunMeta();
  const runState = useRunState();
  const workerStatus = useWorkerStatus();
  const error = useRunError();
  const { dismissRun, requestViewResults, updateActiveRunMeta } = useRunActions();

  // Keep activeRunMeta.projectName in sync with project renames so all
  // consumers of runState.projectName (panel, summary, report) stay current.
  useEffect(() => {
    const handleRename = (e) => {
      if (e.detail?.action !== 'renamed') return;
      if (!e.detail.newName) return;
      if (e.detail.projectId !== activeRunMeta?.projectId) return;
      updateActiveRunMeta({ projectDisplayName: e.detail.newName });
    };
    window.addEventListener('projectCreated', handleRename);
    return () => window.removeEventListener('projectCreated', handleRename);
  }, [activeRunMeta?.projectId, updateActiveRunMeta]);
  const { showSuccess, showError: showErrorToast } = useToastNotificationContext();

  // Capture meta before it gets cleared on completion
  useEffect(() => {
    if (activeRunMeta) {
      lastMetaRef.current = activeRunMeta;
    }
  }, [activeRunMeta]);

  // Reset dismissed state and notification flag when a new run starts
  useEffect(() => {
    if (isRunning) {
      setDismissed(false);
      setShowDetailsPanel(false);
      hasNotifiedRef.current = false;
    }
  }, [isRunning]);

  // Fire toast notification when run completes.
  // The breakdown names skipped and disabled permutations explicitly — reporting
  // "4 passed" for a run where only 2 executed is the whole bug this guards against.
  useEffect(() => {
    if (isCompleted && !hasNotifiedRef.current) {
      hasNotifiedRef.current = true;
      const baseUrl = buildRunUrl(lastMetaRef.current) || '';
      const actionUrl = baseUrl ? `${baseUrl}?showResults=1` : '';

      const parts = [`${passed} passed`, `${failed} failed`];
      if (skipped > 0) parts.push(`${skipped} skipped`);
      if (disabled > 0) parts.push(`${disabled} disabled`);
      const breakdown = `${parts.join(', ')} of ${total} permutation${total === 1 ? '' : 's'}`;

      if (error) {
        showErrorToast('Test Run Failed', error, actionUrl);
      } else if (failed > 0) {
        showErrorToast('Test Run Completed with Failures', breakdown, actionUrl);
      } else {
        showSuccess('Test Run Completed', breakdown, actionUrl);
      }
    }
  }, [isCompleted, error, passed, failed, total, skipped, disabled, showSuccess, showErrorToast]);


  const handleToggleDetails = useCallback(() => {
    setShowDetailsPanel((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setShowDetailsPanel(false);
    setDismissed(true);
    runStreamService.disconnect();
    dismissRun();
  }, [dismissRun]);

  const handleGoToResults = useCallback(() => {
    // Signal AutomationPage to show results + select latest run (via Zustand flag)
    requestViewResults();
    // Navigate to the automation page (if not already there)
    const url = buildRunUrl(activeRunMeta || lastMetaRef.current);
    if (url) navigate(url);
  }, [activeRunMeta, navigate, requestViewResults]);

  if (!isRunActive || dismissed) return null;

  // Error banner (no progress bar)
  if (error && !runState) {
    return (
      <div className="mock-run-error">
        <AlertCircle size={14} />
        <span>{error}</span>
        <button type="button" onClick={handleClose}>&times;</button>
      </div>
    );
  }

  return (
    <div
      className={[
        'mock-run-bottom',
        isRunning ? 'mock-run-bottom--running' : '',
        isCompleted ? 'mock-run-bottom--completed' : '',
        isCompleted && hasFailures ? 'mock-run-bottom--failed' : '',
      ].filter(Boolean).join(' ')}
    >
      <RunProgressBar
        runState={runState}
        isRunning={isRunning}
        isCompleted={isCompleted}
        hasFailures={hasFailures}
        workerStatus={workerStatus}
        isDetailsOpen={showDetailsPanel}
        onToggleDetails={handleToggleDetails}
        onClose={handleClose}
        onGoToResults={handleGoToResults}
      />
      <RunDetailsPanel isOpen={showDetailsPanel} runState={runState} />
    </div>
  );
};

export default GlobalRunProgress;
